import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import type { ParagraphHighlightService, ParagraphHighlightContextMenuItem } from './paragraph-highlight-service.interface';
import ParagraphWording from '@/lib/paragraph-highlight/index';
import { LanguageService } from '@analytics/shared/services/language-service';
import { ContextProxy, GlobalProxy, ModelContextType } from '@patsnap/hiro-event-proxy';

declare module '@/lib/paragraph-highlight/index' {
    export class ParagraphWording {
        static updateDynamicMenu(menu: {
            addPopover: ParagraphHighlightContextMenuItem[],
            delPopover: ParagraphHighlightContextMenuItem[],
        }, isShow: boolean): void;
    }
}



@injectable()
export class ParagraphHighlightServiceImpl implements ParagraphHighlightService {
    protected readonly add2chatMessages = {
        en: 'Ask Hiro',
        cn: '芽仔问答',
        tw: '芽仔問答',
        jp: 'Hiroに質問',
    };

    protected readonly translateMessages = {
        en: 'Translate',
        cn: '翻译',
        tw: '翻譯',
        jp: '翻訳',
    };

    constructor(
        @inject(LanguageService)
        private readonly _languageService: LanguageService,
        @inject(GlobalProxy)
        private readonly _globalProxy: GlobalProxy,
        @inject(ContextProxy)
        private readonly _contextProxy: ContextProxy,
    ) {
    }

    get locale() {
        return this._languageService.getCurrentLanguage();
    }

    get add2chatLabel() {
        return this.add2chatMessages[this.locale];
    }

    get translateLabel() {
        return this.translateMessages[this.locale];
    }

    get addPopover():  ParagraphHighlightContextMenuItem[] {
        const self = this;

        return [
            {
                key: 'translate',
                icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"><path d="M10 12h1.5a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5H10V9h2.5a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5h-4a.5.5 0 00-.5.5v7a.5.5 0 00.5.5h4a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5H10v-1zm6.846-5h1.823a2 2 0 011.977 2.304l-1.385 9A2 2 0 0117.284 20H11a2 2 0 01-2-2H6a2 2 0 01-2-2V6a2 2 0 012-2h8.963a2 2 0 011.982 2.267L16.846 7zm-.269 2l-.978 7.267A2 2 0 0113.617 18H11h6.284l1.385-9h-2.092z" fill="#495973"/><path d="M10 12h1.5a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5H10V9h2.5a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5h-4a.5.5 0 00-.5.5v7a.5.5 0 00.5.5h4a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5H10v-1zm6.846-5h1.823a2 2 0 011.977 2.304l-1.385 9A2 2 0 0117.284 20H11a2 2 0 01-2-2H6a2 2 0 01-2-2V6a2 2 0 012-2h8.963a2 2 0 011.982 2.267L16.846 7zm-.269 2l-.978 7.267A2 2 0 0113.617 18H11h6.284l1.385-9h-2.092z" fill="url(#prefix__paint0_linear_520_2690)"/><defs><linearGradient id="prefix__paint0_linear_520_2690" x1="4" y1="4" x2="4" y2="20" gradientUnits="userSpaceOnUse"><stop stop-color="#18EC81"/><stop offset=".424" stop-color="#0DACBA"/><stop offset="1" stop-color="#0A3DFF"/></linearGradient></defs></svg>',
                label: self.translateLabel,
                callback(text: string) {
                    self._translate(text);
                },
            },
            {
                key: 'add2chat',
                icon: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" id="GptAvatar"><path d="M2.554 12.198a.466.466 0 0 1 .441-.316h1.033c.3 0 .52.278.455.57-.122.544-.268 1.393-.213 2.183.042.603.224 1.111.406 1.482a.466.466 0 0 1-.418.674h-.915a.466.466 0 0 1-.384-.201 4.161 4.161 0 0 1-.709-2.348c0-.978.177-1.673.304-2.044zM19.553 12.453a.467.467 0 0 1 .455-.57h1.032c.2 0 .378.125.442.315.127.371.304 1.066.304 2.044a4.16 4.16 0 0 1-.709 2.348.466.466 0 0 1-.384.2h-.915a.466.466 0 0 1-.418-.672c.182-.372.364-.88.406-1.483.054-.79-.091-1.64-.213-2.182z" fill="#0234CD" /><path d="M20.13 11.201c-.063-.13-.125-.255-.187-.377.186-.647.29-1.947.199-2.718-.25-2.105-1.731-3.94-3.444-3.555-.651.146-.85.968-.809 2.152-1.252-.473-3.22-.536-3.871-.536-.652 0-2.619.063-3.871.536.041-1.184-.158-2.005-.81-2.152-1.712-.384-3.193 1.45-3.443 3.555-.092.771.012 2.071.198 2.718-.061.122-.123.247-.186.377-.711 1.476-1 3.102-.388 4.712.868 2.284 3.19 3.246 5.377 3.487.284.03 1.928.1 3.123.1 1.195 0 2.838-.07 3.123-.1 2.188-.24 4.509-1.203 5.377-3.487.613-1.61.323-3.236-.388-4.712z" fill="url(#GptAvatar_paint0_radial_4082_3553)" /><path d="M18.58 14.411c.291 2.392-1.17 3.23-2.787 3.58-1.125.244-3.172.231-3.775.231-.603 0-2.65.013-3.774-.23-1.618-.35-3.079-1.19-2.787-3.58.227-1.856 1.385-3.706 2.944-4.308.618-.238 1.262-.332 1.882-.38.145-.01 1.735-.064 1.735-.04 0-.024 1.59.03 1.735.04.62.048 1.264.142 1.882.38 1.56.602 2.718 2.452 2.944 4.307z" fill="#fff" /><path fill-rule="evenodd" clip-rule="evenodd" d="M15.957 12.707a.584.584 0 0 1-.108.819l-.742.568.742.568a.584.584 0 1 1-.71.926l-1.346-1.031a.583.583 0 0 1 0-.927l1.346-1.031a.584.584 0 0 1 .818.108z" fill="#020A1A" /><path d="M8.908 15.577a.955.955 0 0 1-.956-.955v-1.146a.955.955 0 1 1 1.911 0v1.146a.955.955 0 0 1-.955.955z" fill="#020A1A" /><defs><radialGradient id="GptAvatar_paint0_radial_4082_3553" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(11.995 16.31) scale(13.8532)"><stop offset=".514" stop-color="#0B64F4" /><stop offset=".73" stop-color="#07D0F5" /><stop offset=".938" stop-color="#18EC81" /></radialGradient></defs></svg>',
                label: self.add2chatLabel,
                callback(text: string) {
                    self._add2chat(text);
                },
            },
        ];
    }

    get delPopover(): ParagraphHighlightContextMenuItem[] {
        return this.addPopover;
    }

    private async _add2chat(text: string) {
        // 防止第一次加载时，hiro 还没加载好，这里需要 await 下
        await this._globalProxy.rpcHiro.call('openSidebar');
        this._globalProxy.rpcHiro.call('setCurrentMenu', 'HiroChat');
        this._contextProxy.rpcHiro.call('addContext', {
            type: ModelContextType.SelectionText,
            value: text,
        });
    }

    private _translate(text: string) {
        this._globalProxy.rpcHiro.call('translate', {
            text,
        });
    }

    updateContextMenu() {
        ParagraphWording.updateDynamicMenu({
            addPopover: this.addPopover,
            delPopover: this.delPopover,
        }, false);
    }
}
